import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Labeled, Modal, TextInput, Toggle } from '@tes/ui';
import { api } from '../../lib/api';
import { messageOf } from '../../lib/errors';
import type { VariableGroupDef } from '../../lib/types';

export function GroupFormDialog({
  initial,
  onClose,
}: {
  initial: VariableGroupDef | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [key, setKey] = useState(initial?.key ?? '');
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');
  const [helpText, setHelpText] = useState(initial?.helpText ?? '');
  const [required, setRequired] = useState(initial?.required ?? true);

  const mutation = useMutation({
    mutationFn: () => {
      const body = { key, prompt, helpText: helpText.trim() || null, required };
      return initial ? api.updateVariableGroup(initial.id, body) : api.createVariableGroup(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['variable-groups'] });
      void qc.invalidateQueries({ queryKey: ['variables'] }); // member rows show the group key
      onClose();
    },
  });

  return (
    <Modal title={initial ? 'Edit choice group' : 'Add choice group'} onClose={onClose} size="sm">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Labeled label="Key (snake_case — n8n can answer the group under this key)">
          <TextInput
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
            placeholder="retailer"
            data-autofocus
          />
        </Labeled>
        <Labeled label="Question">
          <TextInput
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            required
            placeholder="Which electricity retailer is the customer with?"
          />
        </Labeled>
        <Labeled label="Helper text (optional)">
          <TextInput value={helpText} onChange={(e) => setHelpText(e.target.value)} />
        </Labeled>
        <Toggle
          label="Required — creation fails when nothing is picked"
          checked={required}
          onChange={setRequired}
        />
        {initial && key !== initial.key && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
            Renaming updates every member variable. Published versions keep the old key until you
            republish — update callers answering the group under{' '}
            <code className="font-mono">{initial.key}</code>.
          </p>
        )}
        {mutation.error && (
          <p className="text-sm text-red-600">{messageOf(mutation.error, 'Save failed')}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending || !key || !prompt}>
            {initial ? 'Save' : 'Add'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
